import React, { useState } from "react";
import { usePlacesAutocompleteServiceResponse } from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { useRouter } from "next/router";
import { Coordinates, FormValues } from "@/interfaces/searchBlock";
import { getCompanyRateCodeFromQueryParams } from "@/helpers/getCompanyRateCodeFromQueryParams";
import { getSidFromQueryParams } from "@/helpers/getSidFromQueryParams";
import { SearchBlock } from "./SearchBlock";
import { LoadGoogleMapsPlacesScript } from "./LoadGoogleMapsPlacesScript";
import { initialStateValidation } from "./validation/initialStateValidationService";
import usePlacePrediction from "./children/autocomplete/hooks/usePlacePrediction";
import usePeliasPlaces from "./children/autocomplete/hooks/usePeliasPlaces";

type SearchBlockContainerProps = {
  initialState?: FormValues;
  coordinates?: Coordinates;
  loadMapsScriptOnFirstRender?: boolean;
  autoTriggerResearch?: boolean;
  shouldCollapseSearchBlock?: "always" | "whenScrolled" | "never";
  lazyLoadDatePickers?: boolean;
  setIsOneWayTrip?: (value: boolean) => void;
};

export const SearchBlockContainer = ({
  initialState,
  coordinates,
  loadMapsScriptOnFirstRender = false,
  autoTriggerResearch = false,
  shouldCollapseSearchBlock = "never",
  lazyLoadDatePickers = false,
  setIsOneWayTrip,
}: SearchBlockContainerProps) => {
  const { query } = useRouter();
  const [hasStartedLoadingMapsScript, setHasStartedLoadingMapsScript] =
    useState(loadMapsScriptOnFirstRender);
  const [googleMapsPlacesService, setGoogleMapsPlacesService] =
    useState<usePlacesAutocompleteServiceResponse>();

  const placePredictionService = usePlacePrediction();
  const peliasService = usePeliasPlaces();

  // Google maps have a delay to load service
  // To avoid this behavior we have to force a rerendering until the service is loaded
  const [trigger, triggerRerender] = useState(0);

  // check is google placesService is loaded to trigger the rerendering on the first script loading
  // setTimeout to 50ms to minimize google api calls
  if (googleMapsPlacesService?.placesService === null) {
    setTimeout(() => {
      const triggerUpdated = trigger + 1;
      triggerRerender(triggerUpdated);
    }, 50);
  }

  if (initialState) {
    initialState.companyRateCode = getCompanyRateCodeFromQueryParams(query);
    initialState.sid = getSidFromQueryParams(query);
  }

  initialState = initialStateValidation({
    initialState: initialState,
  });

  return (
    <>
      <SearchBlock
        initialState={initialState}
        coordinates={coordinates}
        loadGoogleMapsScript={() => {
          setHasStartedLoadingMapsScript(true);
        }}
        placesService={googleMapsPlacesService?.placesService}
        refreshSessionToken={googleMapsPlacesService?.refreshSessionToken}
        placePredictionService={placePredictionService}
        peliasService={peliasService}
        autoTriggerResearch={autoTriggerResearch}
        shouldCollapseSearchBlock={shouldCollapseSearchBlock}
        lazyLoadDatePickers={lazyLoadDatePickers}
        setIsOneWayTrip={setIsOneWayTrip}
      />
      {hasStartedLoadingMapsScript && (
        <LoadGoogleMapsPlacesScript
          setGoogleMapsPlacesService={setGoogleMapsPlacesService}
        />
      )}
    </>
  );
};
